export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || __API_BASE_URL__ || 'http://127.0.0.1:8002').replace(/\/$/, '')
/* 访客聊天端（zhiying-chat）独立部署：dev 固定 5174；生产未配置 VITE_CHAT_BASE_URL 时按站点同源。 */
const chatBase = import.meta.env.VITE_CHAT_BASE_URL ?? (import.meta.env.DEV ? 'http://127.0.0.1:5174' : (typeof window !== 'undefined' ? window.location.origin : ''))
export const CHAT_BASE_URL = chatBase.replace(/\/$/, '')

export type SnowflakeId = string
export type JsonRecord = Record<string, unknown>

declare global {
  interface ImportMetaEnv { readonly VITE_API_BASE_URL?: string; readonly VITE_CHAT_BASE_URL?: string }
  interface ImportMeta { readonly env: ImportMetaEnv }
  const __API_BASE_URL__: string
}

let csrfToken = ''

/** 企业被产品中心停用时，由请求层通知应用壳层切换到全局停用门屏。 */
export const ENTERPRISE_DISABLED_EVENT = 'zhiying:enterprise-disabled'

export function setCsrfToken(token: unknown) { csrfToken = typeof token === 'string' ? token : '' }

export class ApiError extends Error {
  constructor(message: string, public readonly status = 0, public readonly body?: unknown) {
    super(message)
    this.name = 'ApiError'
  }
}

export function apiUrl(path: string) {
  return `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`
}

function normalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(normalize)
  if (!value || typeof value !== 'object') return value
  const row = value as Record<string, unknown>
  const result: Record<string, unknown> = {}
  for (const [key, child] of Object.entries(row)) {
    if (key === 'isdelete' && [true, 1, '1', 'true'].includes(child as never)) continue
    if (key === 'isdelete' && [false, 0, '0', 'false'].includes(child as never)) continue
    const idField = key === 'id' || key.endsWith('_id') || key === 'memory_lib_id' || key === 'customer_id' || key === 'contact_id' || key === 'agent_id'
    result[key] = idField && child != null ? String(child) : normalize(child)
  }
  return result
}

/** Backend success bodies are {code,message,data,...} envelopes; the auth SDK writes plain JSON. */
function unwrapEnvelope(body: unknown): unknown {
  if (body && typeof body === 'object' && !Array.isArray(body)) {
    const record = body as Record<string, unknown>
    if ('code' in record && 'data' in record) return record.data
  }
  return body
}

/** 后端 SubBindingFilter 使用稳定错误码 A0231 表示企业已停用。 */
function isEnterpriseDisabledBody(body: unknown) {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return false
  const record = body as Record<string, unknown>
  return String(record.code ?? '') === 'A0231'
    || String(record.message ?? '').trim() === '本企业已停用'
}

/** 供状态层区分“企业停用”和普通请求失败，避免把停用误判为登录失效。 */
export function isEnterpriseDisabledError(error: unknown) {
  return error instanceof ApiError && isEnterpriseDisabledBody(error.body)
}

async function request<T>(path: string, init: RequestInit = {}, allowUnauthorized = false): Promise<T> {
  const headers = new Headers(init.headers)
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
  const method = (init.method || 'GET').toUpperCase()
  if (csrfToken && !['GET', 'HEAD', 'OPTIONS'].includes(method)) headers.set('X-Dongli-CSRF', csrfToken)
  let response: Response
  try {
    response = await fetch(apiUrl(path), { ...init, headers, credentials: 'include' })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError('请求超时，请稍后重试')
    }
    throw new ApiError(`无法连接管理服务（${API_BASE_URL}）`)
  }
  const text = await response.text()
  let body: unknown
  if (text) {
    try { body = JSON.parse(text) } catch { body = text }
  }
  if (!response.ok) {
    if (isEnterpriseDisabledBody(body) && typeof window !== 'undefined') {
      window.dispatchEvent(new Event(ENTERPRISE_DISABLED_EVENT))
    }
    if (response.status === 401 && allowUnauthorized) throw new ApiError('尚未登录', 401, body)
    const record = body && typeof body === 'object' ? body as Record<string, unknown> : {}
    throw new ApiError(String(record.message || record.error || (typeof body === 'string' ? body : `请求失败（${response.status}）`)), response.status, body)
  }
  if (text && typeof body === 'string') {
    throw new ApiError(`管理服务返回了非 JSON 响应（HTTP ${response.status}），请检查 API 地址和反向代理配置`, response.status, body)
  }
  return normalize(unwrapEnvelope(body)) as T
}

export const api = {
  get: <T = unknown>(path: string, allowUnauthorized = false, init: RequestInit = {}) => request<T>(path, init, allowUnauthorized),
  post: <T = unknown>(path: string, data?: unknown) => request<T>(path, { method: 'POST', body: JSON.stringify(data ?? {}) }),
  put: <T = unknown>(path: string, data: unknown) => request<T>(path, { method: 'PUT', body: JSON.stringify(data) }),
  patch: <T = unknown>(path: string, data: unknown) => request<T>(path, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: <T = unknown>(path: string) => request<T>(path, { method: 'DELETE' })
}

export function rows(value: unknown): JsonRecord[] {
  const root = value && typeof value === 'object' ? value as Record<string, unknown> : {}
  const list = Array.isArray(value) ? value : root.items ?? root.rows ?? root.records ?? root.data
  return Array.isArray(list) ? list.filter(item => item && typeof item === 'object') as JsonRecord[] : []
}

export function object(value: unknown): JsonRecord {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value as JsonRecord
  return {}
}

export const statusNames: Record<string,string> = { active: '启用中', archived: '已归档', inactive: '已停用', disabled: '已停用', draft: '草稿', pending: '待处理', consulting: '咨询中', ready: '在线', blocked: '已阻塞', closed: '已结束' }

export function errorText(error: unknown) {
  return error instanceof Error ? error.message : '请求失败，请重试'
}

export function formatDate(value: unknown) {
  if (!value) return '—'
  const date = new Date(String(value))
  return Number.isNaN(date.getTime()) ? String(value) : new Intl.DateTimeFormat('zh-CN', { dateStyle: 'medium', timeStyle: 'short' }).format(date)
}
